// R97 — THE FIELD EVERY READING ON THIS SITE IS BUILT FROM HAD NO FAILURE STATE, AND THE ONE HONEST
// COPY OF IT LIVES IN ONE ROOM.
//
// `/api/astrology/geo-search` is read from TWO places: `BirthPlaceFields` — the birth city field
// R12-1 pulled out because *every* reading form on this site is built from it, signup included — and
// `PlaceSearch`, the astrocartography room's own picker. Both did the same three-step unwrap and
// **neither of them looked at `response.ok`**:
//
//     const data = await response.json();
//     setResults(Array.isArray(data.results) ? data.results : []);
//
// So the birth field had exactly TWO states, `searching` and not, and drew this under an empty list:
//
//     "No matches yet. Keep typing."
//
// **THAT IS A SENTENCE ABOUT HER TOWN, PRINTED OVER A LOOKUP THAT NEVER ANSWERED** — R60's Card
// Library finding and R64's Cauldron finding word for word, on the one field a member cannot get
// past. It is worse than either of those two, for a reason that is a property of this field rather
// than of its copy: `buildAstrologyPayload` refuses a chart with no coordinates, and coordinates
// arrive ONLY by picking a row off this list. **So the failure is terminal AND misattributed** — she
// is told this house does not know where she was born, and instructed to keep typing at a lookup
// that will never answer, on the form she fills in to exist here (R12).
//
// **AND THE HONEST COPY WAS HONEST ABOUT THE WRONG HALF.** AC2 built the astrocartography picker a
// real five-state `lookup` with its own "did not answer" sentence — the capability living only on
// the surface that needed it first, which is W12/R26/R35/R38's shape — but its `error` arm is
// reachable only from a `catch`, i.e. from a request that never landed or a body that would not
// parse. `handleGeoSearch` refuses with a real status and a real sentence
// (`{ message: "Place lookup failed." }`), that body parses perfectly, and `Array.isArray(...)` is
// false, so **the refusal the server composed landed on "No match found." in the room that had the
// state for it.** One law, `response.ok` read, both copies.
//
// **AND `source` IS READ NOW, WHICH IS R94's FINDING ONE ROUTE OVER.** The route sends
// `{ results: [], source: "none" }` on exactly one path: no Geoapify answer AND no
// `ASTROLOGY_API_KEY`, i.e. **this house has no geocoder left to ask**. That is not "no such place",
// and it was arriving as one. It is unreachable in a configured environment, which is precisely why
// it would never have been noticed in one.
//
// **THE ABORT RULING IS THE LOAD-BEARING PART AND IT MOVED IN HERE ON PURPOSE.** I1's newest-keystroke
// AbortController lives on the birth field, and its own comment says an abort is *"a NEWER keystroke
// winning, not a failure: leave the results and the searching state alone so the field does not
// flicker empty between characters."* That ruling was written at a call site, where nothing could run
// it, and it is the one ruling in this file a "fix" would plausibly undo — an aborted request looks
// exactly like a failed one from inside the `catch`. `lookupStateFor` takes the PREVIOUS state and
// hands it straight back on an abort, so the ruling is arithmetic a gate drives rather than a
// sentence in a comment.
//
// `doFetch` is a PARAMETER (R46), so every answer this route can give is reachable with no browser,
// no vendor key and no network. This module imports NOTHING (the entry requirement for a
// `scripts/`-loadable law).

export const placeSearchPath = "/api/astrology/geo-search";

// WHAT THE LOOKUP CAME BACK WITH. `none` is an ANSWER — the geocoder was asked and knows no such
// place — while `unavailable` is the house failing to ask at all. Collapsing those two is the whole
// defect, so they are two words here and never one boolean (ITEM 7's `self` argument, R56's third
// copy state, R18's distinction).
export type PlaceLookupKind = "found" | "none" | "unavailable" | "aborted";

// WHAT THE FIELD DRAWS. `idle` and `searching` are the caller's own facts (nothing typed yet, a
// request in flight); the other three are the answer's. `aborted` is deliberately NOT here: an
// abort is not something a field draws, it is a reason to draw nothing new.
export type PlaceLookupState = "idle" | "searching" | "found" | "none" | "unavailable";

export type PlaceLookupAnswer<T> = {
  kind: PlaceLookupKind;
  results: T[];
  // What the SERVER said, when it said anything. Kept apart from the field's own copy: this route
  // composes "Place lookup failed." on one path and nothing at all on the others, and a room that
  // prints an empty string has printed a sentence with no words in it (R51/R66).
  message: string;
};

const answer = <T,>(kind: PlaceLookupKind, results: T[] = [], message = ""): PlaceLookupAnswer<T> => ({
  kind, results, message,
});

export type PlaceSearchQuery = {
  // The birth forms bias to the country she already chose; the astrocartography picker reads any
  // place on earth and passes `scope: "anywhere"`. Both are the ROUTE's own parameters, spelled
  // once here rather than twice at two call sites 4,000 lines apart (R31/R60).
  countryCode?: string;
  scope?: string;
  signal?: AbortSignal;
};

export async function searchPlaces<T>(
  place: string,
  query: PlaceSearchQuery = {},
  doFetch: typeof fetch = typeof fetch === "function" ? fetch : (undefined as never),
): Promise<PlaceLookupAnswer<T>> {
  let response: Response;
  try {
    response = await doFetch(placeSearchPath, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ place, countryCode: query.countryCode || "", ...(query.scope ? { scope: query.scope } : {}) }),
      ...(query.signal ? { signal: query.signal } : {}),
    });
  } catch {
    // I1's ruling, and it is asked of the SIGNAL rather than of the error, because a DOMException
    // name is a thing a polyfill or a test double can spell differently and the signal cannot.
    if (query.signal?.aborted) return answer<T>("aborted");
    // R18/R63: a request that never landed is TRANSPORT NOISE, not the geocoder's word about her town.
    return answer<T>("unavailable");
  }
  const data = await response.json().catch(() => null) as
    | { results?: unknown; source?: unknown; message?: unknown }
    | null;
  const said = typeof data?.message === "string" ? data.message : "";
  // **PRESENCE, NEVER TRUTHINESS** (R53). A refusal here carries a status AND no `results` array,
  // and either one on its own is enough: a proxy can hand back a cached body under a failing status,
  // and this route's own 200 paths can only ever carry the array.
  if (!response.ok || !data || !Array.isArray(data.results)) return answer<T>("unavailable", [], said);
  // R94's `source`, one route over. "none" is the house with no geocoder left to ask, and it rides
  // on a 200 with an empty array — byte-identical, without this line, to knowing no such place.
  if (data.source === "none") return answer<T>("unavailable", [], said);
  const rows = data.results as T[];
  return answer<T>(rows.length ? "found" : "none", rows, said);
}

// WHAT THE FIELD DRAWS NEXT, given what came back and what it was drawing. One function, so the
// abort ruling cannot be true of one copy of this field and false of the other.
export function lookupStateFor<T>(a: PlaceLookupAnswer<T>, previous: PlaceLookupState): PlaceLookupState {
  return a.kind === "aborted" ? previous : a.kind;
}

// Whether the results already on screen still stand. An abort leaves them exactly where they were
// (I1: no flicker between keystrokes); every other answer replaces them, INCLUDING a refusal —
// rows resolved against a query she has since changed are not evidence about the query she is
// typing now.
export function keepsResults<T>(a: PlaceLookupAnswer<T>): boolean {
  return a.kind === "aborted";
}

// R103 — **WHICH SENTENCE THE PICKER DRAWS, AND IT IS A FIVE-WAY DECISION THAT LIVED AS A TERNARY
// CHAIN INSIDE A THREE-HOOK COMPONENT.** The room's own five states were correct and nothing could
// run them; the round that translated them is the round that made them reachable (R64's Cauldron
// panel one control down). The KEY is returned rather than the words, because this module imports
// nothing — which is the entry requirement that lets a gate load it — and because 14-B3's seam says
// the catalog is the one place a sentence lives.
//
// **THE PLACE'S OWN LABEL IS A TOKEN, NEVER GLUED TO AN ENGLISH WORD ORDER** (14-B5's argument): it
// is the geocoder's formatted name and this house does not reformat it (R24).
export type PlaceNote = { key: string; className: string; vars?: Record<string, string> };

export function placeNote(lookup: PlaceLookupState, picked: { label?: string } | null): PlaceNote {
  if (picked) return { key: "acg.place.picked", className: "acg-place__picked", vars: { place: picked.label || "" } };
  // AC2/AC3 and R97 are three DIFFERENT facts and stay three sentences: nothing was found, the
  // lookup did not answer, and we are still asking. Collapsing any two of them is the defect each
  // was written to close.
  if (lookup === "none") return { key: "acg.place.none", className: "acg-place__none" };
  if (lookup === "unavailable") return { key: "acg.place.unavailable", className: "acg-place__none" };
  if (lookup === "searching") return { key: "acg.place.searching", className: "acg-place__hint" };
  return { key: "acg.place.hint", className: "acg-place__hint" };
}

// ---------------------------------------------------------------------------
// IT #182 — **THE HINT THAT ONLY EVER FIRED FOR A LEGACY ROW, AND THE MEMBER WHO NEEDED IT WAS
// TYPING FREE TEXT.**
//
// `rshillpinney@mindspring.com` wrote in unable to add a person of interest: *"Anthony Sutton DOB
// 07/06/1962 ... Place of birth: Newark NJ."* The birth city field draws its dropdown only while it
// is OPEN, so a member who types a town, gets *No matches yet*, and clicks away is left looking at a
// filled-in field with **no coordinates behind it** and nothing on screen saying so. She saves, and
// the refusal arrives much later at the chart — which INPUT-1/INPUT-1b measured landing as HER fault.
//
// The field already had the right sentence and asked the wrong question for it:
//
//     const needsConfirm = fromFill && display.trim().length > 0 && (!latitude || !longitude);
//
// **`fromFill` MEANS *THIS TEXT CAME FROM A STORED ROW*, so the hint was reachable only for legacy
// rows and never for the member typing** — the state that actually produces a dead end. The FACT that
// matters is *there is text and no coordinates behind it*, and where that text came from is not part
// of it (R18: two facts, and only one of them is this decision's).
//
// **THREE OUTCOMES, NOT TWO** (R18, and `placeNote`'s own five-way one module up):
//   · still searching -> NOTHING, because a hint drawn between keystrokes is a nag about an answer
//     that has not arrived.
//   · the lookup did not answer -> the lookup's own sentence, because *pick your town from the list*
//     is an instruction she cannot follow when there is no list to pick from (R97's whole finding).
//   · anything else with no coordinates -> the pick-a-place hint, and it PERSISTS below the field
//     after the dropdown closes, which is the half that was missing.
//
// Returns a KEY rather than words, like `placeNote`, because this module imports nothing.
// The field's own threshold, declared ONCE. The hint below and the search above have to agree about
// what counts as a query, or a member gets told to pick from a list nothing has been searched for.
export const PLACE_MIN_QUERY = 2;

export function placePickNote(
  { text, hasCoordinates, lookup }: { text: string; hasCoordinates: boolean; lookup: PlaceLookupState },
): PlaceNote | null {
  const typed = String(text || "").trim();
  if (typed.length < PLACE_MIN_QUERY) return null;
  if (hasCoordinates) return null;
  if (lookup === "searching") return null;
  if (lookup === "unavailable") return { key: "place.lookupFailed", className: "birthplace-field__confirm" };
  return { key: "place.confirmCity", className: "birthplace-field__confirm" };
}
